import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from '@3plearning/react-components';
import { t } from '@lingui/macro';
import LocalStorage from '../../helpers/local-storage';
import {
  SearchableListWrapper,
  InputWrapper,
  SelectPanelWrapper,
  showElement,
  SearchContainer,
  FilterWrapper,
  FilteredListWrapper
} from './styled';
import SvgIcon from '../svg-icon/svg-icon';
import { FilterIcon, FilterOutlineIcon } from '../../helpers/icon-dictionary';
import SelectableListItem from '../selectable-list-item';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import { CHECKED, UNCHECKED } from '../checkbox/constants';
import {
  UPDATE_TOPICS,
  SHOW_HIDE_SEARCH_PANEL,
  APPLY_FILTER
} from '../../containers/course-assign/course-assign-types';
import { useTracking } from '../../analytics';

const FilterDropDownList = (props) => {
  const { i18n, onApply } = props;
  const dispatch = useDispatch();
  const { trackTopicApplied } = useTracking();

  const hideShowSearchPanel = (status) => {
    dispatch({
      type: SHOW_HIDE_SEARCH_PANEL,
      status
    });
  };
  const updateTopics = (
    actionType,
    id,
    checked,
    term,
    checkedNormal,
    checkedEasier
  ) => {
    dispatch({
      type: UPDATE_TOPICS,
      id,
      checked,
      searchTerm: term,
      actionType,
      checkedNormal,
      checkedEasier
    });
  };

  const activeClass = useSelector(
    (state) => state.CourseAssignReducer.activeClass
  );
  const activeGroup = useSelector(
    (state) => state.CourseAssignReducer.activeGroup
  );

  const showSearchPanel = useSelector(
    (state) => state.CourseAssignReducer.showSearchPanel
  );

  const selectedTopicCount = useSelector(
    (state) => state.CourseAssignReducer.selectedTopicCount
  );

  const selectedCourseTopics = useSelector(
    (state) => state.CourseAssignReducer.selectedCourseTopics
  );

  const applyChanges = () => {
    const lsKey = `LAST_FILTERSEARCH_SELECTION_${activeClass}_${activeGroup}`;
    LocalStorage.setItem(lsKey, selectedCourseTopics);
    dispatch({
      type: APPLY_FILTER
    });
  };

  const [searchTerm, setSearchTerm] = useState('');
  const searchTextbox = useRef(null);

  const label = i18n._(
    t(
      'filterDropdownList.filterByTopics'
    )`Showing ${selectedTopicCount} of ${selectedCourseTopics.length} items, click here to filter`
  );

  const selectButtonLabel =
    selectedTopicCount !== 0
      ? i18n._(t('filterDropdownList.unSelectAll')`Unselect all`)
      : i18n._(t('filterDropdownList.selectAll')`Select all`);

  const handleClick = () => {
    if (selectedCourseTopics.length) {
      hideShowSearchPanel(true);
      searchTextbox.current.placeholder = i18n._(
        t('filterDropdownList.searchphText')`Type here to search`
      );
    }
  };

  const handleOnChange = (e) => {
    const searchText = e.target.value;
    setSearchTerm(searchText);
    updateTopics('onSearch', '', false, searchText);
    if (!searchText) {
      searchTextbox.current.placeholder = i18n._(
        t(
          'filterDropdownList.searchStandards'
        )`Type here to search for Standards`
      );
    }
  };

  const uncheckAll = () => {
    if (selectedTopicCount !== 0) {
      updateTopics('uncheckAll');
    } else {
      updateTopics('checkAll');
    }
  };

  const applyFilter = () => {
    trackTopicApplied({ topicCount: selectedTopicCount });
    hideShowSearchPanel(false);
    applyChanges();
    searchTextbox.current.placeholder = label;
    onApply();
  };

  const onSelect = (checked, id) => {
    updateTopics('selection', id, checked, searchTerm);
  };

  const onRadioSelect = (id, checkedNormal, checkedEasier) => {
    updateTopics(
      'radioupdate',
      id,
      '',
      searchTerm,
      checkedNormal,
      checkedEasier
    );
  };
  const getCheckedState = (item) => {
    if (item.selected) {
      return CHECKED;
    }

    return UNCHECKED;
  };

  const renderTopicsItems = () => {
    if (selectedCourseTopics) {
      return selectedCourseTopics.map((item) => {
        if (!item.isHidden) {
          return (
            <span key={item.topicId}>
              <div className="strand">
                <div className="wrapper">
                  <div className="Description">
                    <SelectableListItem
                      i18n={i18n}
                      id={item.topicId}
                      checked={getCheckedState(item)}
                      name={item.topicName}
                      onChange={onSelect}
                      easierActivitiesCount={item.easierActivities.length}
                      checkedNormal={item.checkedNormal}
                      checkedEasier={item.checkedEasier}
                      onRadioselect={onRadioSelect}
                      isChecked={item.selected}
                    />
                  </div>
                </div>
              </div>
              <hr className="line" />
            </span>
          );
        }
        return null;
      });
    }

    return null;
  };

  const focusSearchTextbox = () => {
    searchTextbox.current.placeholder = label;
    if (showSearchPanel) {
      hideShowSearchPanel(false);
    }
  };

  const msgNoFilterApplies = i18n._(
    t(
      'filterDropdownList.noFilterApplied'
    )`No filters have been applied - showing all topics`
  );
  const msgFilterApplies = i18n._(
    t(
      'filterDropdownList.filterApplied'
    )`Filters have been applied - click search bar to see which topics have been excluded`
  );
  const logoTitle =
    selectedTopicCount === selectedCourseTopics.length
      ? msgNoFilterApplies
      : msgFilterApplies;

  return (
    <>
      <OutsideClickHandler
        callback={focusSearchTextbox}
        forButton="searchPanel"
      >
        <div id="searchPanel">
          <SearchContainer>
            <FilterWrapper>
              <label htmlFor="filter">
                {i18n._(t('filterDropdownList.topicfilter')`Topic Filter`)}
              </label>
              <SearchableListWrapper>
                <div className="searchText">
                  <InputWrapper
                    ref={searchTextbox}
                    type="text"
                    onClick={handleClick}
                    onChange={handleOnChange}
                    value={searchTerm}
                    placeholder={label}
                    data-name="filter"
                  />
                </div>
                <div className="searchLogo" title={logoTitle}>
                  {selectedTopicCount === selectedCourseTopics.length ? (
                    <SvgIcon icon={FilterOutlineIcon} />
                  ) : (
                    <FilteredListWrapper>
                      <SvgIcon icon={FilterIcon} />
                    </FilteredListWrapper>
                  )}
                </div>
              </SearchableListWrapper>
            </FilterWrapper>
          </SearchContainer>
          <SelectPanelWrapper style={showElement(showSearchPanel)}>
            <div>
              <div className="panelBody">{renderTopicsItems()}</div>
              <div className="panelFooter">
                <div className="uncheck">
                  <Button
                    className="unCheckAllButton"
                    disabled={!selectedCourseTopics.length}
                    text={selectButtonLabel}
                    type={Button.LOZENGE}
                    onClick={uncheckAll}
                  />
                </div>
                <div className="apply">
                  <Button
                    className="applyButton"
                    disabled={!selectedCourseTopics.length}
                    primary
                    text={i18n._(
                      t('filterDropdownList.applyFilter')`Apply filter`
                    )}
                    type={Button.LOZENGE}
                    onClick={applyFilter}
                  />
                </div>
              </div>
            </div>
          </SelectPanelWrapper>
        </div>
      </OutsideClickHandler>
    </>
  );
};

FilterDropDownList.propTypes = {
  i18n: PropTypes.object,
  onApply: PropTypes.func
};

export default FilterDropDownList;



// WEBPACK FOOTER //
// ./components/filter-dropdownlist/index.js